import { createSelector } from 'reselect';
import { MOET_CONSTANTS } from 'moet-core';

import { appState } from './utils';

const { PRODUCT_PRICING_TYPES, DESIRED_PRODUCT_SORTING } = MOET_CONSTANTS;

const sortProductsFn = (p1, p2) => {
  const indexP1 = DESIRED_PRODUCT_SORTING.indexOf(p1.id);
  const indexP2 = DESIRED_PRODUCT_SORTING.indexOf(p2.id);
  if (indexP1 < 0) {
    return 1;
  }
  if (indexP2 < 0) {
    return -1;
  }
  return indexP1 - indexP2;
};

const productsNameSpace = createSelector(appState, ({ products }) => products);

const getAllProducts = createSelector(productsNameSpace, ({ data = [] }) => {
  return data.map(product => ({
    ...product,
    name: `${product.id}.name`,
    description: `${product.id}.description`,
    options: product.options
      .filter(p => p.id !== 'kco-address-widget')
      .map(option => {
        return {
          ...option,
          name: `${product.id}.options.${option.id}.name`,
          description: `${product.id}.options.${option.id}.description`,
        };
      }),
  }));
});

export const getProductData = createSelector(productsNameSpace, ({ data = [] }) => data);

export const getSortedProducts = createSelector(getAllProducts, products =>
  products
    .sort(sortProductsFn)
    .map(product => ({
      ...product,
      options: product.options.filter(p => p.id !== 'kco-address-widget'),
    }))
    .filter(prod => prod.options.length > 0),
);

export const getCompatibleAndHiddenProducts = createSelector(getAllProducts, products =>
  products
    .map(p => ({
      ...p,
      options: p.options.filter(
        ({ id, isCountryCompatible }) => id !== 'kco-address-widget' && isCountryCompatible,
      ),
    }))
    .filter(({ options }) => options.length),
);

export const getCompatibleProducts = createSelector(getCompatibleAndHiddenProducts, products =>
  products
    .map(p => ({
      ...p,
      options: p.options.filter(({ hideFromSelection }) => !hideFromSelection),
    }))
    .filter(({ hideFromSelection }) => !hideFromSelection),
);

export const getCompatibleProductsFlattened = createSelector(getCompatibleProducts, products => {
  return products.reduce((acc, product) => {
    if (product.id === 'kco' || product.id.includes('one')) {
      return [...acc, { ...product, options: product.options }];
    }
    if (product.id === 'kp') {
      return [...acc, ...product.options];
    }
  }, []);
});

export const getCompatibleAndSortedProducts = createSelector(getCompatibleProducts, products =>
  products.sort(sortProductsFn),
);

export const getCompatibleHiddenAndSortedProducts = createSelector(
  getCompatibleAndHiddenProducts,
  products => products.sort(sortProductsFn),
);

export const getSelectedProductId = createSelector(
  productsNameSpace,
  ({ selectedProductId }) => selectedProductId,
);

export const getSelectedProduct = createSelector(
  getAllProducts,
  getSelectedProductId,
  (products, selectedProductId) =>
    products && selectedProductId ? products.find(p => p.id === selectedProductId) : null,
);

export const getSelectedProductOptionIds = createSelector(
  productsNameSpace,
  ({ selectedProductOptionIds }) => selectedProductOptionIds,
);

export const getExpandedProductId = createSelector(
  productsNameSpace,
  getCompatibleAndSortedProducts,
  ({ expandedProductId }, products) => {
    let product = products.find(p => p.id === expandedProductId);
    if (product && product.isCompatible) {
      return expandedProductId;
    } else {
      product = products.find(p => p.isCompatible);
      return product ? product.id : null;
    }
  },
);

export const getCompatibleProductIds = createSelector(getCompatibleAndSortedProducts, products =>
  products
    .filter(prod => prod.isCompatible)
    .flatMap(p => p.options.filter(p => !p.notAvailableForBusiness).map(p => p.id)),
);

export const getParentPricedProducts = createSelector(
  getCompatibleHiddenAndSortedProducts,
  products =>
    products.filter(
      product => product.pricing === PRODUCT_PRICING_TYPES.PARENT && product.isCompatible,
    ),
);

export const getChildrenPricedProducts = createSelector(
  getCompatibleHiddenAndSortedProducts,
  products =>
    products.filter(
      product => product.pricing === PRODUCT_PRICING_TYPES.CHILDREN && product.isCompatible,
    ),
);

export const getProductsError = createSelector(productsNameSpace, ({ error = null }) => error);

export const isKlarnaOneAvailable = createSelector(
  getCompatibleProductIds,
  // If there is any klarna one product on the list of products, it means klarna one is available
  // for the current country
  productIds => productIds.find(i => i && i.includes('_one')) !== undefined,
);
